// Desktop-only preferences are available from Edit even before backend login.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DesktopApiPreferences } from '../electron';

const DesktopPreferences: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [settings, setSettings] = useState<DesktopApiPreferences | null>(null);
    const [url, setUrl] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const api = window.electronAPI;

    useEffect(() => api?.onPreferencesRequested(() => setOpen(true)), [api]);

    useEffect(() => {
        const labels = t('desktopMenu', { returnObjects: true }) as Record<string, string>;
        api?.setMenuLabels({ ...labels, preferences: t('desktopPreferences.menu') }).catch(console.error);
    }, [api, t, i18n.resolvedLanguage]);

    useEffect(() => {
        if (!open || !api) return;
        let active = true;
        setBusy(true);
        setError('');
        setSaved(false);
        setSettings(null);
        api.getApiPreferences().then((value) => {
            if (!active) return;
            setSettings(value);
            setUrl(value.savedUrl || value.defaultUrl);
        }).catch(() => {
            if (active) setError('desktopPreferences.loadError');
        }).finally(() => {
            if (active) setBusy(false);
        });
        return () => { active = false; };
    }, [open, api]);

    useEffect(() => {
        if (open) dialogRef.current?.showModal();
        else dialogRef.current?.close();
    }, [open]);

    if (!api) return null;

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        let normalized: string;
        try {
            const parsed = new URL(url.trim());
            if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password
                || parsed.pathname !== '/' || parsed.search || parsed.hash) throw new Error('Invalid origin');
            normalized = parsed.origin;
        } catch {
            setError('desktopPreferences.invalidUrl');
            return;
        }
        setBusy(true);
        try {
            const value = await api.setApiUrl(normalized);
            setUrl(value);
            setSaved(true);
        } catch {
            setError('desktopPreferences.saveError');
        } finally {
            setBusy(false);
        }
    };

    return (
        <dialog ref={dialogRef} aria-labelledby="desktop-preferences-title"
            className="m-auto w-[min(560px,calc(100%-32px))] rounded-xl border border-border bg-background p-6 text-foreground shadow-xl backdrop:bg-black/50"
            onCancel={(event) => { if (busy) event.preventDefault(); else setOpen(false); }}>
            <form onSubmit={handleSave} noValidate className="space-y-4">
                <h2 id="desktop-preferences-title" className="text-xl font-semibold">{t('desktopPreferences.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('desktopPreferences.description')}</p>
                <label className="block space-y-2">
                    <span className="text-sm font-medium">{t('desktopPreferences.apiUrl')}</span>
                    <input autoFocus type="url" value={url} disabled={busy || !settings}
                        className="w-full rounded-md border border-border bg-background px-3 py-2"
                        onChange={(event) => { setUrl(event.target.value); setSaved(false); }} />
                </label>
                {settings && <>
                    <p className="break-all text-sm">{t('desktopPreferences.defaultUrl', { url: settings.defaultUrl })}</p>
                    <p className="text-sm text-muted-foreground">{t('desktopPreferences.priority')}</p>
                    {settings.overrideSource && <p role="note" className="break-all rounded-md border border-amber-500 p-3 text-sm">
                        {t('desktopPreferences.override', { source: settings.overrideSource, url: settings.effectiveUrl })}
                    </p>}
                </>}
                <p className="text-sm text-muted-foreground">{t('desktopPreferences.restartHint')}</p>
                {error && <p role="alert" className="text-sm text-red-600">{t(error)}</p>}
                {saved && <p role="status" className="text-sm text-green-600">{t('desktopPreferences.saved')}</p>}
                <footer className="flex flex-wrap justify-end gap-2">
                    <button type="button" disabled={busy || !settings} className="rounded-md border border-border px-3 py-2 text-sm"
                        onClick={() => { setUrl(settings!.defaultUrl); setSaved(false); setError(''); }}>{t('desktopPreferences.reset')}</button>
                    <button type="button" disabled={busy} className="rounded-md border border-border px-3 py-2 text-sm"
                        onClick={() => setOpen(false)}>{t('desktopPreferences.close')}</button>
                    <button type="submit" disabled={busy || !settings} className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50">
                        {t(busy ? 'desktopPreferences.loading' : 'desktopPreferences.save')}
                    </button>
                </footer>
            </form>
        </dialog>
    );
};

export default DesktopPreferences;